import React from "react";
import { map, size } from "lodash";
import Dropzone from "react-dropzone";
import styled, { css } from "styled-components";
import {
  Spacing,
  border,
  size as cssSize,
  darken,
  dominant,
  message,
} from "@glossy";
import { tr } from "@linkfluence/i18n";
import { Locale } from "@linkfluence/models/locale";
import Button from "@ui-kit/button";
import Icon from "@ui-kit/icon";
import Modal, { CustomButton } from "@ui-kit/modal";
import Tooltip from "@ui-kit/tooltip/description";

const stopPropagation = (callback, id) => (event) => {
  event.preventDefault();
  event.stopPropagation();
  callback(id);
};

interface IProps {
  onCancel: () => void;
  onValidate: (picture: { id?: number; url?: string } | null) => void;
  onUpload: (files: File[]) => void;
  onDelete: (picture: number) => void;
  pictures?: Array<{ id: number; url: string }>;
  picture?: number;
  locale: Locale;
}

const Upload = styled(Dropzone)`
  display: inline-block;
`;

const Delete = styled(Icon)`
  color: ${(props) => message(props, "error")};
  position: absolute;
  top: 4px;
  right: 4px;
  &:hover {
    color: ${(props) => darken(0.1, message(props, "error"))};
  }
`;

const Picture = styled.div<{ active: boolean }>`
  position: relative;
  display: inline-block;
  margin: 0 ${Spacing.Medium} ${Spacing.Medium} 0;
  box-sizing: border-box;
  cursor: pointer;
  opacity: 0.5;
  background-size: cover;
  background-clip: content-box;
  border: solid 1px ${border};
  border-radius: 84px;
  filter: grayscale(1);
  ${cssSize(84)};
  ${(props) =>
    props.active &&
    css`
      border: solid 2px ${dominant};
      opacity: 1;
      filter: grayscale(0);
    `}
  &:hover {
    border: solid 2px ${dominant};
    opacity: 1;
    filter: grayscale(0);
  }
  &:not(:hover) ${Delete} {
    display: none;
  }
`;

const KomonModalAvatar = ({
  onCancel,
  onValidate,
  onDelete,
  onUpload,
  pictures,
  picture,
  locale,
}: IProps) => {
  const buttons: CustomButton[] = [
    <Upload key="dropzone" onDrop={onUpload} accept="image/*">
      <Button
        text={tr(locale, "MODAL_AVATAR::IMPORT_NEW_PICTURE")}
        variant="primary"
      />
    </Upload>,
    {
      label: tr(locale, "MODAL_AVATAR::CANCEL"),
      action: onCancel,
      variant: "tertiary",
    },
  ];

  if (size(pictures) > 0) {
    buttons.unshift({
      label: tr(locale, "MODAL_AVATAR::RESET_DEFAULT_PICTURE"),
      variant: "primary",
      action: () => onValidate && onValidate({}),
    });
  }

  return (
    <Modal
      title={tr(locale, "MODAL_AVATAR::SELECT_PROFILE_IMAGE")}
      description={
        size(pictures)
          ? undefined
          : tr(locale, "MODAL_AVATAR::NOT_UPLOADED_YET")
      }
      customButtons={buttons}
      onCancel={onCancel}
      locale={locale}
    >
      {size(pictures) > 0 && (
        <div>
          {map(pictures, ({ id, url }) => (
            <Picture
              active={id === picture}
              key={id}
              style={{ backgroundImage: `url(${url})` }}
              onClick={() => onValidate && onValidate({ id, url })}
            >
              <Tooltip content={tr(locale, "MODAL_AVATAR::DELETE")}>
                <Delete glyph="close" onClick={stopPropagation(onDelete, id)} />
              </Tooltip>
            </Picture>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default KomonModalAvatar;
